import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Role } from '@/config/constants';
import type { LinkedAccount } from '@/services/auth.service';

interface AuthUser {
  id: string;
  /** Alias for `id` — populated by the server response normalisation in setAuth */
  _id?: string;
  name: string;
  email: string;
  role: Role;
  isVerified: boolean;
  avatarUrl?: string;
  phone?: string;
  savedProperties?: string[];
}

interface AuthState {
  user: AuthUser | null;
  token: string | null;
  refreshToken: string | null;
  sessionId: string | null;
  isAuthenticated: boolean;
  linkedAccounts: LinkedAccount[];
  setAuth: (user: AuthUser, token: string, refreshToken?: string, sessionId?: string) => void;
  updateToken: (token: string) => void;
  updateUser: (user: Partial<AuthUser>) => void;
  clearAuth: () => void;
  setLinkedAccounts: (accounts: LinkedAccount[]) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      token: null,
      refreshToken: null,
      sessionId: null,
      isAuthenticated: false,
      linkedAccounts: [],

      setAuth: (user, token, refreshToken, sessionId) => {
        const raw = user as AuthUser & { _id?: string };
        const normalised: AuthUser = { ...user, id: user.id ?? raw._id ?? '' };
        set({
          user: normalised,
          token,
          refreshToken: refreshToken ?? null,
          sessionId: sessionId ?? null,
          isAuthenticated: true,
        });
      },

      updateToken: (token) => set({ token }),

      updateUser: (updates) =>
        set((state) => ({ user: state.user ? { ...state.user, ...updates } : null })),

      clearAuth: () =>
        set({ user: null, token: null, refreshToken: null, sessionId: null, isAuthenticated: false, linkedAccounts: [] }),

      setLinkedAccounts: (accounts) => set({ linkedAccounts: accounts }),
    }),
    {
      name: 'cmbrs_auth',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        refreshToken: state.refreshToken,
        sessionId: state.sessionId,
        isAuthenticated: state.isAuthenticated,
        linkedAccounts: state.linkedAccounts,
      }),
    },
  ),
);

